import { useState, useEffect } from "react";
import { supabase } from "../../../../../integrations/supabase/client";
import { Button } from "../../../../ui/button";
import { useToast } from "../../../../../hooks/use-toast";
import { Plus, Loader2 } from "lucide-react";
import OutletCard from "./OutletCard";
import OutletModal from "./OutletModal";
import { DataForSEOOutletModal } from "./DataForSEOOutletModal";
import { useOutletRefresh } from "../../../../../hooks/useOutletRefresh";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../ui/alert-dialog";

interface Outlet {
  id: string;
  outlet_name: string;
  location?: string;
  manager_name?: string;
  email?: string;
  phone?: string;
  google_maps_url?: string;
  instagram_username?: string;
  tiktok_username?: string;
  facebook_url?: string;
  tripadvisor_url?: string;
  created_at: string;
  data_source?: string;
}
interface outletProps {
  outletList:string[];
  onRecallOulet: (recall: boolean) => void;
  supabaseoutletIDs:string[];
}


const OutletManagement = ({ outletList, onRecallOulet, supabaseoutletIDs }: outletProps) => {
  const { toast } = useToast();
  const { refreshOutlet } = useOutletRefresh();
  const [loading, setLoading] = useState(true);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [dataForSEOModalOpen, setDataForSEOModalOpen] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState<Outlet | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [outletToDelete, setOutletToDelete] = useState<string | null>(null);
  const [balanceOutlet, setbalanceOutlet] = useState(0);

  useEffect(() => {
    loadOutlets();
    const emptyCount = (outletList as any[]).filter(
  item => !item.supabaseOutletID || item.supabaseOutletID.trim() === ""
).length;
    setbalanceOutlet(emptyCount)
  }, []);

  const loadOutlets = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("outlets")
        .select("*").in('id', supabaseoutletIDs)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setOutlets(data || []);
    } catch (error: any) {
      console.error("Error loading outlets:", error);
      toast({
        title: "Error",
        description: "Failed to load outlets",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutlet = async (outlet: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to manage outlets",
          variant: "destructive",
        });
        return;
      }

      const outletData = {
        ...outlet,
        user_id: user.id,
        google_maps_url: outlet.google_maps_url || null,
        instagram_username: outlet.instagram_username || null,
        tiktok_username: outlet.tiktok_username || null,
        facebook_url: outlet.facebook_url || null,
        tripadvisor_url: outlet.tripadvisor_url || null,
      };

      let outletId: string;
      const isUpdate = !!outlet.id;

      if (isUpdate) {
        const { error } = await supabase
          .from("outlets")
          .update(outletData)
          .eq("id", outlet.id);

        if (error) throw error;
        
        outletId = outlet.id;

        // Trigger a fresh scrape after update to apply changes
        await refreshOutlet({
          outletId,
          outletName: outlet.outlet_name,
          isDataForSEO: false,
          onSuccess: () => {
            toast({
              title: "Success",
              description: "Outlet updated and reviews refreshed!",
            });
          },
        });
      } else {
        const { data: insertData, error } = await supabase
          .from("outlets")
          .insert([outletData])
          .select()
          .single();

        if (error) {
          // Check for duplicate constraint violation
          if (error.message?.includes('outlets_user_place_unique') || error.code === '23505') {
            throw new Error("This outlet already exists for your account. Please use the existing outlet instead.");
          }
          throw error;
        }

        outletId = insertData.id;

        // Trigger initial scrape after creation
        await refreshOutlet({
          outletId,
          outletName: outlet.outlet_name,
          isDataForSEO: false,
          onSuccess: () => {
            toast({
              title: "Success",
              description: "Outlet created and initial scrape completed!",
            });
          },
        });
      }

      // Force refresh outlets from database
      await loadOutlets();
      setModalOpen(false);
      setSelectedOutlet(null);
    } catch (error: any) {
      console.error("Error saving outlet:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save outlet",
        variant: "destructive",
      });
    }
  };

  const handleDeleteOutlet = async () => {
    if (!outletToDelete) return;

    try {
      const { error } = await supabase
        .from("outlets")
        .delete()
        .eq("id", outletToDelete);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Outlet deleted successfully!",
      });

      loadOutlets();
    } catch (error: any) {
      console.error("Error deleting outlet:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete outlet",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setOutletToDelete(null);
    }
  };

  const handleEdit = (outlet: any) => {
    setSelectedOutlet(outlet);
    // Check if this is a DataForSEO outlet
    if (outlet.data_source === 'dataforseo') {
      setDataForSEOModalOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  const handleDeleteClick = (id: string) => {
    setOutletToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleAddNew = () => {
    setSelectedOutlet(null);
    setModalOpen(true);
  };

  const handleAddDataForSEO = () => {
    setSelectedOutlet(null);
    setDataForSEOModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Outlet Management Setting</h2>
          <p className="text-muted-foreground mt-1">
            Manage your outlets and configure review scraping URLs
          </p>
        </div>
        <div className="flex gap-2">
          {/* <Button onClick={handleAddNew} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Outlet
          </Button> */}
          {balanceOutlet>0 &&
          <Button onClick={handleAddDataForSEO} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Connect Outlet
          </Button>
          }
        </div>
      </div>

      {outlets.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <h3 className="text-lg font-semibold mb-2">No outlets yet</h3>
          <p className="text-muted-foreground mb-4">
            Add your first outlet to start tracking reviews from multiple locations
          </p>
          <Button onClick={handleAddDataForSEO}>
            <Plus className="h-4 w-4 mr-2" />
            Add Outlet
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {outlets.map((outlet) => (
            <OutletCard
              key={outlet.id}
              outlet={outlet}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onRefresh={loadOutlets}
            />
          ))}
        </div>
      )}


      <OutletModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedOutlet(null);
        }}
        onSave={handleSaveOutlet}
        outlet={selectedOutlet}
      />

      <DataForSEOOutletModal
        open={dataForSEOModalOpen}
        onOpenChange={(open) => {
          setDataForSEOModalOpen(open);
          if (!open) setSelectedOutlet(null);
        }}
        onSuccess={loadOutlets}
        outlet={selectedOutlet?.data_source === 'dataforseo' ? selectedOutlet : undefined}
        outletList={outletList}
        onRecallOulet={(recall) => {
          onRecallOulet(recall);
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Outlet</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this outlet? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOutlet}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OutletManagement;
